// api.js — CS Client Overview API client
// Base URL from env var: process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL

import { dedupe } from '../../../services/dedupe';
import { getUser } from '../../../auth/authService';
import { tracked, newId } from '../../../services/workTelemetry';

const ANALYTICS_HOST = process.env.REACT_APP_ANALYTICS_MICROSERVICE_URL ?? '';
const BASE = `${ANALYTICS_HOST}/api/cs`;
const ALERTS_BASE = `${ANALYTICS_HOST}/api/alerts`;

// To-Do lives in the Chat microservice (routers/client_overview_router.py),
// not Analytics — everything else in this file talks to Analytics.
const CHAT_HOST = process.env.REACT_APP_CHAT_MICROSERVICE_URL ?? '';
const TODO_BASE = `${CHAT_HOST}/client-overview`;
const ADO_BASE = `${CHAT_HOST}/client-overview/ado`;
const NEXIFY_BASE = `${CHAT_HOST}/nexify`;

async function authHeaders() {
  const user = await getUser();
  return {
    'Content-Type': 'application/json',
    Authorization: user?.access_token ? `Bearer ${user.access_token}` : '',
  };
}

async function parseError(res) {
  try {
    const body = await res.json();
    return body?.detail ?? `HTTP ${res.status}`;
  } catch {
    const text = await res.text().catch(() => '');
    if (!text || text.trimStart().startsWith('<')) return `HTTP ${res.status}`;
    return text.slice(0, 300);
  }
}

// includeInactive=true also pulls in offboarded/inactive clients (isActive:false),
// which the API hides by default. The list page fetches everything once and
// filters client-side with the "Show inactive" toggle, so it doesn't refetch.
export async function fetchClients(includeInactive = true) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients?include_inactive=${includeInactive ? 'true' : 'false'}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns { clients: [...] }
}

// The client list's Needs signals (#4323): { asOf, thresholds, sources, clients: { [id lowercased]: {overdue, usage, contact, renewal} } }.
// Activation tiles (ADO #4334): per district, seats / chat / chat depth / dashboards over a window,
// against team-wide thresholds. { days, windows, thresholds, asOf, clients: {id: {seats, chat, depth,
// dashboards: {status: ok|none, pct, num, den, tripped}}}, totals }.
export async function fetchClientActivation(days, refresh = false) {
  const headers = await authHeaders();
  const q = new URLSearchParams();
  if (days) q.set('days', String(days));
  if (refresh) q.set('refresh', 'true');
  const res = await fetch(`${BASE}/clients/needs/activation${q.toString() ? `?${q}` : ''}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchClientNeeds(refresh = false) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/needs${refresh ? '?refresh=true' : ''}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Human email with one client (shared mailboxes ops@ + support@ + implementations@): one row per thread.
// { threads: [{ conversationId, date, mailbox, mailboxes, subject, count, direction, participants, webLink }],
//   unavailable: [mailbox...], lookbackDays, mailboxes }.  Read-only.
// The CS journey for one client with a proposed date per stage and what it counts from
// (GET /api/cs/clients/{id}/journey). { kickoffDate, stages: [{label, anchor, days, show_in_meetings,
// proposed, basis, held: [{date, source, label}], latest}], meetings_unavailable, meetings_seen }
export async function fetchJourney(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/journey`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchEmailThreads(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/email-threads`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// One message's text, read from Outlook when a person opens it in the Email lane.
// Never stored; plain text only. { messageId, mailbox, text, uniqueText, contentType }.
export async function fetchEmailMessageBody(clientId, rowId, messageId) {
  const headers = await authHeaders();
  const qs = `row_id=${encodeURIComponent(rowId)}&message_id=${encodeURIComponent(messageId)}`;
  const res = await fetch(`${BASE}/clients/${clientId}/email-body?${qs}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Every message's body on one thread, oldest first, read from Outlook when the
// reader opens, as Outlook's own HTML (sanitised before rendering, emailHtml.js).
// Never stored. { rowId, messages: [{messageId, uniqueHtml, html, mailbox} | {messageId, error}], unread: [...] }
export async function fetchEmailThreadText(clientId, rowId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/email-thread-text?row_id=${encodeURIComponent(rowId)}&format=html`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchClient(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function saveClient(clientId, data) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchUsage(clientId, days) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/usage?days=${days}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchFeatureUsage(clientId, days) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/feature-usage?days=${days}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Cross-client adoption overview (Session-Intelligence style). Returns global
// totals + per-district rows, each with current/previous/changePct for the 5
// metrics, plus a tool funnel. `days` ∈ {7,14,30,60,90}.
export async function fetchUsageOverview(days, excludeAdmin = true) {
  const headers = await authHeaders();
  const qs = `days=${days}&exclude_admin=${excludeAdmin ? 'true' : 'false'}`;
  const res = await fetch(`${BASE}/usage-overview?${qs}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Per-client granular activity: { topTools, topDashboards, topPages, topNodes }.
export async function fetchActivity(clientId, days, excludeAdmin = true) {
  const headers = await authHeaders();
  const qs = `days=${days}&exclude_admin=${excludeAdmin ? 'true' : 'false'}`;
  const res = await fetch(`${BASE}/clients/${clientId}/activity?${qs}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function fetchActiveUsers(clientId, days) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/active-users?days=${days}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export function fetchClientUsers(clientId) {
  // Two panels ask for this on the same mount: one request serves both (services/dedupe.js).
  return dedupe(`client-users:${clientId}`, async () => {
    const headers = await authHeaders();
    const res = await fetch(`${BASE}/clients/${clientId}/users`, { headers });
    if (!res.ok) throw new Error(await parseError(res));
    return res.json(); // { users: [{id, name, email}] }
  });
}

// Per-client audit trail (Azure Table Storage, newest first).
export async function fetchAudit(clientId, limit = 200) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/audit?limit=${limit}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { audit: [{by, at, action}] }
}

export async function appendAudit(clientId, action) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/audit`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ action }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { by, at, action }
}

// Cross-client to-do list — every to-do across every client, newest first.
export async function fetchAllTodos() {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { todos: [{id, title, description, checkinDate, checkinWith, clientId, clientName, assignedTo, priority, done, isDeleted, createdBy, createdOn, updatedBy, updatedOn, isGrouped?, clients?}] }
}

// Per-client to-do list.
export async function fetchTodos(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/clients/${clientId}/todos`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { todos: [{id, title, description, checkinDate, checkinWith, clientId, assignedTo, priority, done, isDeleted, createdBy, createdOn, updatedBy, updatedOn}] }
}

// Single to-do by id — used by the deep-linkable To-Do popup (?todo={id}).
export async function fetchTodo(todoId) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function createTodoUntracked(clientId, data) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/clients/${clientId}/todos`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Cross-client create — data.clientIds (array) or data.allClients (bool)
// assigns the same to-do to multiple clients at once, one independent row
// per client. Returns { todos: [...] } (may be more than one row).
async function createTodosForClientsUntracked(data) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos`, {
    method: 'POST',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function updateTodoUntracked(clientId, todoId, data) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/clients/${clientId}/todos/${todoId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Fetch all notes on a to-do, newest first — the frontend reveals them in
// batches client-side as the user scrolls.
export async function fetchTodoNotes(todoId) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/notes`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { notes: [{id, todoId, note, createdBy, createdOn, updatedBy, updatedOn, edited, mine, history}], total, historyUnavailable }
}

// Edit a note you wrote (ADO #4318). The server checks you are its author
// (403 otherwise) and keeps the replaced text as an earlier version.
async function updateTodoNoteUntracked(todoId, noteId, note) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/notes/${noteId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Endpoint to create new note or new comment for todo id
async function createTodoNoteUntracked(todoId, note) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/notes`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}
// Download historical usage as a zip (user_summary.csv + user_event_counts.csv)
// for [start, end] (YYYY-MM-DD). Streams the file to a browser download.
export async function downloadUsageExport(clientId, start, end) {
  const headers = await authHeaders();
  const qs = `start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}`;
  const res = await fetch(`${BASE}/clients/${clientId}/usage/export?${qs}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition') || '';
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename = match ? match[1] : `usage_${start}_to_${end}.zip`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Proposal-parser generative spend: { recentRuns, mtdUsd, mtdRuns, allTimeUsd, allTimeRuns }
export async function fetchGenerativeCosts(limit = 50) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/generative-costs?limit=${limit}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Alerts (management.NexusWorkFlow, Type='Alert') — schedule/recipients/links live
// in the alert's config JSON; see analytics_api's tools_api/alerts_router.py.
export async function fetchAlerts() {
  const headers = await authHeaders();
  const res = await fetch(`${ALERTS_BASE}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns [{ workflow_id, name, is_active, client_id, has_config, send_status }]
}

// Most recent scheduled sends (newest first, max 26) with per-recipient state.
export async function fetchAlertSends(workflowId, limit = 8) {
  const headers = await authHeaders();
  const res = await fetch(`${ALERTS_BASE}/${workflowId}/sends?limit=${limit}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns [{ scheduled_utc, status, expected, sent, failed, recipients: [{ key, email, state, sent_utc, reason }] }]
}

export async function fetchAlert(workflowId) {
  const headers = await authHeaders();
  const res = await fetch(`${ALERTS_BASE}/${workflowId}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns { workflow_id, name, is_active, client_id, config }
}

export async function createAlert(clientId, name) {
  const headers = await authHeaders();
  const res = await fetch(`${ALERTS_BASE}`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ client_id: clientId, name }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns { workflow_id, name, is_active, is_client_visible, client_id, config }
}

export async function saveAlert(workflowId, { isActive, isClientVisible, config } = {}) {
  const headers = await authHeaders();
  const body = {};
  if (isActive !== undefined) body.is_active = isActive;
  if (isClientVisible !== undefined) body.is_client_visible = isClientVisible;
  if (config !== undefined) body.config = config;
  const res = await fetch(`${ALERTS_BASE}/${workflowId}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Permanent: also removes the alert's send history and its links' tracking tokens.
export async function deleteAlert(workflowId) {
  const headers = await authHeaders();
  const res = await fetch(`${ALERTS_BASE}/${workflowId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(await parseError(res)); // 404 not found, 400 not an alert
  // 204 No Content
}

export async function parseProposal(clientId, file) {
  const user = await getUser();
  const formData = new FormData();
  formData.append('file', file);
  const res = await fetch(`${BASE}/clients/${clientId}/parse-proposal`, {
    method: 'POST',
    headers: { Authorization: user?.access_token ? `Bearer ${user.access_token}` : '' },
    body: formData,
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // returns extracted fields
}

// "Nexify" (polish with AI) a to-do's description. Returns the rewritten
// description as a plain string (matches the other /nexify/* endpoints).
async function nexifyTodoDescriptionUntracked(input, title) {
  const headers = await authHeaders();
  const res = await fetch(`${NEXIFY_BASE}/nexifyTodoDescription`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ input, title }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// The shared Nexify prompt template used by every "Nexify" click on a to-do
// description — { prompt }.
export async function fetchNexifyTodoDescriptionPrompt() {
  const headers = await authHeaders();
  const res = await fetch(`${NEXIFY_BASE}/todoDescriptionPrompt`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function updateNexifyTodoDescriptionPromptUntracked(prompt) {
  const headers = await authHeaders();
  const res = await fetch(`${NEXIFY_BASE}/todoDescriptionPrompt`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ prompt }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Tickets linked to a to-do — order is insertion order (oldest first), no
// paging (a to-do realistically links a handful of tickets, not hundreds).
export async function fetchTodoTickets(todoId) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/tickets`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { tickets: [{id, todoId, ticketNumber, createdBy, createdOn}] }
}

async function linkTodoTicketUntracked(todoId, ticketNumber) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/tickets`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ ticketNumber }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function unlinkTodoTicketUntracked(todoId, ticketLinkId) {
  const headers = await authHeaders();
  const res = await fetch(`${TODO_BASE}/todos/${todoId}/tickets/${ticketLinkId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

export async function searchAdoWorkItems(q, top = 10) {
  const headers = await authHeaders();
  const qs = `q=${encodeURIComponent(q)}&top=${top}`;
  const res = await fetch(`${ADO_BASE}/search?${qs}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json(); // { q, count, results: [{id, title, type, state, assigned_to, changed_on, url}] }
}

export async function fetchAdoWorkItem(ticketNumber) {
  const headers = await authHeaders();
  const res = await fetch(`${ADO_BASE}/workitem/${encodeURIComponent(ticketNumber)}`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// --- Client links (Analytics: tools_api/cs_client_links_router.py) ---

// Read a client's links, already in display order: [{id, label, url, createdBy, createdOn, updatedBy, updatedOn}].
export async function fetchClientLinks(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/links`, { headers });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function createClientLinkUntracked(clientId, { url, label }) {
  const headers = await authHeaders();
  const requestBody = JSON.stringify({ url, label });
  const res = await fetch(`${BASE}/clients/${clientId}/links`, {
    method: 'POST',
    headers,
    body: requestBody,
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

// Only the fields present in `changes` are sent; `label: null` clears the label.
async function updateClientLinkUntracked(clientId, linkId, changes) {
  const headers = await authHeaders();
  const requestBody = JSON.stringify(changes);
  const res = await fetch(`${BASE}/clients/${clientId}/links/${linkId}`, {
    method: 'PATCH',
    headers,
    body: requestBody,
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json();
}

async function deleteClientLinkUntracked(clientId, linkId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/links/${linkId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) throw new Error(await parseError(res));
  // 204 No Content
}

// Build the telemetry description for a client-link write; a new link's id comes from the response.
function describeClientLinkWrite(args, result) {
  const [clientId, second] = args;
  const linkId = typeof second === 'string' ? second : newId(result);
  return {
    resource: `client-link:${linkId || 'new'}`,
    details: {
      source: 'client-link',
      item_id: linkId,
      client_id: clientId,
    },
  };
}

// --- Client files (Analytics: tools_api/cs_client_files_router.py) ---

// Throw an Error that carries the HTTP status, so the Files card can tell
// "no storage container" (409) apart from a failed read (502).
async function throwResponseError(res) {
  const message = await parseError(res);
  const error = new Error(message);
  error.status = res.status;
  throw error;
}

// Read a client's files: [{id, displayName, fileName, size, contentType, uploadedBy, uploadedOn, uploadFinished}].
export async function fetchClientFiles(clientId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/files`, { headers });
  if (!res.ok) await throwResponseError(res);
  return res.json();
}

// Get a fresh 30-minute download URL; fetched per click because old ones expire.
export async function fetchClientFileDownloadUrl(clientId, fileId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/files/${fileId}/download-url`, { headers });
  if (!res.ok) await throwResponseError(res);
  const body = await res.json();
  return body.url;
}

// Get a 15-minute write URL for one new blob. Not tracked: nothing is saved
// until completeClientFileUpload, which is.
export async function requestClientFileUploadUrl(clientId, { fileName, size, contentType }) {
  const headers = await authHeaders();
  const requestBody = JSON.stringify({ fileName, size, contentType });
  const res = await fetch(`${BASE}/clients/${clientId}/files/upload-url`, {
    method: 'POST',
    headers,
    body: requestBody,
  });
  if (!res.ok) await throwResponseError(res);
  return res.json(); // { fileId, uploadUrl }
}

// Record the display name and uploader once the blob is uploaded.
async function completeClientFileUploadUntracked(clientId, fileId, displayName) {
  const headers = await authHeaders();
  const requestBody = JSON.stringify({ displayName });
  const res = await fetch(`${BASE}/clients/${clientId}/files/${fileId}/complete`, {
    method: 'POST',
    headers,
    body: requestBody,
  });
  if (!res.ok) await throwResponseError(res);
  return res.json();
}

// Change a file's display name; the stored file name stays the same.
async function renameClientFileUntracked(clientId, fileId, displayName) {
  const headers = await authHeaders();
  const requestBody = JSON.stringify({ displayName });
  const res = await fetch(`${BASE}/clients/${clientId}/files/${fileId}`, {
    method: 'PATCH',
    headers,
    body: requestBody,
  });
  if (!res.ok) await throwResponseError(res);
  return res.json();
}

// Delete a file's blob. Permanent unless the storage account has soft delete on.
async function deleteClientFileUntracked(clientId, fileId) {
  const headers = await authHeaders();
  const res = await fetch(`${BASE}/clients/${clientId}/files/${fileId}`, {
    method: 'DELETE',
    headers,
  });
  if (!res.ok) await throwResponseError(res);
  // 204 No Content
}

// Build the telemetry description shared by the client-file writes.
function describeClientFileWrite([clientId, fileId, displayName]) {
  return {
    resource: `client-file:${fileId}`,
    details: {
      source: 'client-file',
      item_id: fileId,
      client_id: clientId,
      display_name: displayName,
    },
  };
}

// --- Telemetry: every write above is recorded as a work_* event (services/workTelemetry.js).
export const createClientLink = tracked('create', createClientLinkUntracked, describeClientLinkWrite);
export const updateClientLink = tracked('update', updateClientLinkUntracked, describeClientLinkWrite);
export const deleteClientLink = tracked('delete', deleteClientLinkUntracked, describeClientLinkWrite);
export const createTodo = tracked('create', createTodoUntracked, ([clientId, data], r) => ({ resource: `todo:${newId(r) || 'new'}`, details: { source: 'todo', item_id: newId(r), client_id: clientId, fields: data } }));
export const createTodosForClients = tracked('create', createTodosForClientsUntracked, ([data], r) => ({ resource: 'todo:bulk', details: { source: 'todo', bulk: true, item_ids: (r?.todos || []).map(newId), fields: data } }));
export const updateTodo = tracked('update', updateTodoUntracked, ([clientId, todoId, data]) => ({ resource: `todo:${todoId}`, details: { source: 'todo', item_id: String(todoId), client_id: clientId, changes: data } }));
export const createTodoNote = tracked('note', createTodoNoteUntracked, ([todoId, note]) => ({ resource: `todo:${todoId}`, details: { source: 'todo', item_id: String(todoId), note: String(note || '').slice(0, 1000) } }));
export const updateTodoNote = tracked('note_edit', updateTodoNoteUntracked, ([todoId, noteId, note]) => ({ resource: `todo:${todoId}`, details: { source: 'todo', item_id: String(todoId), note_id: String(noteId), note: String(note || '').slice(0, 1000) } }));
export const linkTodoTicket = tracked('todo_ticket_link', linkTodoTicketUntracked, ([todoId, ticketNumber]) => ({ resource: `todo:${todoId}`, details: { source: 'todo', item_id: String(todoId), ticket: ticketNumber } }));
export const unlinkTodoTicket = tracked('todo_ticket_unlink', unlinkTodoTicketUntracked, ([todoId, linkId]) => ({ resource: `todo:${todoId}`, details: { source: 'todo', item_id: String(todoId), ticket_link_id: linkId } }));
export const nexifyTodoDescription = tracked('todo_nexify', nexifyTodoDescriptionUntracked, () => ({ resource: 'todo:nexify' }));
export const updateNexifyTodoDescriptionPrompt = tracked('settings_save', updateNexifyTodoDescriptionPromptUntracked, () => ({ resource: 'work-setting:nexify_todo_prompt', details: { key: 'nexify_todo_prompt' } }));
export const completeClientFileUpload = tracked('create', completeClientFileUploadUntracked, describeClientFileWrite);
export const renameClientFile = tracked('update', renameClientFileUntracked, describeClientFileWrite);
export const deleteClientFile = tracked('delete', deleteClientFileUntracked, describeClientFileWrite);
